import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Login from './pages/Login'
import UserPage from './pages/UserPage'
import MinistryPage from './pages/MinistryPage'
import AdminPage from './pages/AdminPage'
import EventsOverviewPage from './pages/EventsOverviewPage'
import SysAdminPage from './pages/SysAdminPage'
import PublicPage from './pages/PublicPage'
import SpeakerUploadPage from './pages/SpeakerUploadPage'
import Layout from './components/Layout'

// Protected Route Component
function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <div className="container flex-center" style={{ height: '100vh' }}>Loading...</div>
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const roleHierarchy = { viewer: 1, user: 2, admin: 3, sysadmin: 4 }
  const userLevel = roleHierarchy[user.role] || 0
  const requiredLevel = roleHierarchy[requiredRole] || 0

  if (userLevel < requiredLevel) {
    if (user.role === 'sysadmin') return <Navigate to="/sysadmin" replace />
    if (user.role === 'admin' || user.role === 'viewer') return <Navigate to="/admin" replace />
    return <Navigate to="/event" replace />
  }

  return children
}

// Public Route - Redirects to appropriate page if already logged in
function PublicRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <div className="container flex-center" style={{ height: '100vh' }}>Loading...</div>
  }

  if (user) {
    const roleHierarchy = { viewer: 1, user: 2, admin: 3, sysadmin: 4 }
    const userLevel = roleHierarchy[user.role] || 0

    if (userLevel >= 4) {
      return <Navigate to="/sysadmin" replace />
    }
    if (user.role === 'viewer') {
      return <Navigate to="/admin" replace />
    }
    if (userLevel >= 3) {
      return <Navigate to="/admin" replace />
    }
    return <Navigate to="/event" replace />
  }

  return children
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Route - Events listing (root) */}
      <Route path="/" element={<PublicPage />} />

      {/* Speaker upload (password gate) */}
      <Route path="/upload" element={<SpeakerUploadPage />} />

      {/* Login Route */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* User Routes - Event Entry (view-only users use /admin only) */}
      <Route
        path="/event"
        element={
          <ProtectedRoute requiredRole="user">
            <Layout>
              <UserPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* 家事報告 — all logged-in users, own church only */}
      <Route
        path="/ministry"
        element={
          <ProtectedRoute requiredRole="user">
            <Layout>
              <MinistryPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="viewer">
            <Layout>
              <AdminPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/events"
        element={
          <ProtectedRoute requiredRole="viewer">
            <Layout>
              <EventsOverviewPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* SysAdmin Routes */}
      <Route
        path="/sysadmin"
        element={
          <ProtectedRoute requiredRole="sysadmin">
            <Layout>
              <SysAdminPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sysadmin/user"
        element={
          <ProtectedRoute requiredRole="sysadmin">
            <Layout>
              <SysAdminPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}